/**
 * Short, consistent how-to copy for vendor list/tool pages.
 */
export const VENDOR_PAGE_GUIDES = {
  invoices: {
    title: 'How invoices work',
    steps: [
      'Create a custom invoice (or open one from a booking).',
      'Add your logo, line items, optional discount, and tax.',
      'Send it by email or download a PDF for the client.',
    ],
  },
  contracts: {
    title: 'How contracts work',
    steps: [
      'Create a contract from a template or AI draft.',
      'Add your logo and optionally sign as the vendor.',
      'Send the signing link or email — the client signs online.',
    ],
  },
  contractCreate: {
    title: 'Building this contract',
    steps: [
      'Choose a template or generate a draft with AI.',
      'Fill client details and review the contract body.',
      'Sign as the vendor (optional), save, then send for the client’s signature.',
    ],
  },
  invoiceCreate: {
    title: 'Building this invoice',
    steps: [
      'Add bill-to details and line items.',
      'Set logo, discount, and tax if needed.',
      'Save, then email or download the PDF.',
    ],
  },
  workspace: {
    title: 'Make Workspace your daily home',
    steps: [
      'Add to-dos and meetings, then drag cards across To do → In progress → Done.',
      'Filter the board by type, and check today’s bookings beside it.',
      'Open Automations for recipe workflows — tiered by plan.',
    ],
  },
  automations: {
    title: 'Automations that stick',
    steps: [
      'Enable a recipe that matches how you sell (leads, invoices, events).',
      'Build When → If (channel/amount/days) → Then (board, WhatsApp, agent, or lead stage) (Pro).',
      'Connect WhatsApp and Sheets under Integrations — tiered by plan.',
    ],
  },
  integrations: {
    title: 'Connect once, reuse everywhere',
    steps: [
      'Connect WhatsApp with your business number and edit message templates (Pro).',
      'Paste a Google Sheets URL, share with the platform account, Sync now (Pro). Optional Inbox tab pulls rows into Workspace.',
      'Enable agents so Workspace can draft follow-ups you approve (Pro).',
    ],
  },
  bookings: {
    title: 'Managing bookings',
    steps: [
      'New requests appear here when clients book your services.',
      'Open a booking to accept, message, or mark the event complete.',
      'Use Calendar for a month view of upcoming events.',
    ],
  },
  privateBookings: {
    title: 'Private bookings',
    steps: [
      'Log off-platform jobs you handle yourself — or import many at once from CSV.',
      'Download the import template, fill rows in Excel, then Save As CSV UTF-8 to upload.',
      'Private bookings stay on your calendar and invoices, outside marketplace payouts.',
    ],
  },
  services: {
    title: 'List your services',
    steps: [
      'Add each offer clients can book (e.g. photo booth package).',
      'Set pricing, duration, and what is included.',
      'Optionally group offers into packages for easier checkout.',
    ],
  },
  packages: {
    title: 'Service packages',
    steps: [
      'Bundle services into packages clients can choose at checkout.',
      'Packages show on your public profile when active.',
      'Keep pricing clear so clients know what they are paying for.',
    ],
  },
  discountCodes: {
    title: 'Discount codes',
    steps: [
      'Create a code with a percent or fixed amount off.',
      'Set usage limits and an expiry date if needed.',
      'Share the code with clients — it applies at checkout.',
    ],
  },
  earnings: {
    title: 'Understanding earnings',
    steps: [
      'Totals update as platform bookings are paid and completed.',
      'Use Payments for individual payment records.',
      'Payouts from admin appear under Payouts when disbursed.',
    ],
  },
  payments: {
    title: 'Payment records',
    steps: [
      'See each payment tied to a booking or invoice.',
      'Filter or search when you need a specific receipt.',
      'Platform fees and net amounts are shown per payment when available.',
    ],
  },
  payouts: {
    title: 'Payouts',
    steps: [
      'These are disbursements sent to you by the platform.',
      'Filter by date or search to find a specific payout.',
      'Export CSV/PDF when you need records for accounting.',
    ],
  },
  accounting: {
    title: 'Your marketplace books',
    steps: [
      'Credits are order earnings after platform fees; debits are payouts you received.',
      'Available balance is what the platform still owes you.',
      'Use Earnings, Payments, and Payouts for deeper detail or exports.',
    ],
  },
  portfolio: {
    title: 'Build your portfolio',
    steps: [
      'Upload your best work so clients trust your style.',
      'Keep images high quality and relevant to your services.',
      'A strong portfolio helps conversion on your public profile.',
    ],
  },
  gallery: {
    title: 'Photo gallery',
    steps: [
      'Upload general business photos (not just portfolio highlights).',
      'Reuse gallery images for branding, invoices, and contracts.',
      'Keep file sizes reasonable for faster loading.',
    ],
  },
  calendar: {
    title: 'Your calendar',
    steps: [
      'Filter Internal bookings, External bookings, or client leads (both channels).',
      'Drag a lead or External booking onto another day to reschedule; pin leads and add notes from the detail panel.',
      'Use Agenda on mobile for a simpler list, and open a day to see everything scheduled.',
    ],
  },
  consultations: {
    title: 'Lead management',
    steps: [
      'Open Consultation form to enable booking on your services and edit the questionnaire.',
      'New requests start as Pending — review details, then schedule a meeting.',
      'After Meeting Done, follow-up reminders appear here until the lead is booked or closed.',
    ],
  },
  reviews: {
    title: 'Client reviews',
    steps: [
      'Reviews appear after completed bookings.',
      'Respond professionally when a reply option is available.',
      'Strong ratings improve trust on your public profile.',
    ],
  },
  analytics: {
    title: 'Analytics',
    steps: [
      'Filter All / Internal / External to see marketplace vs private jobs.',
      'Change the period to compare weeks or months.',
      'Use top services and trends to decide what to promote.',
    ],
  },
  advancedAnalytics: {
    title: 'Advanced analytics',
    steps: [
      'Deeper charts across Internal and External channels (paid plan).',
      'Compare completion, earnings, and peak hours by channel.',
      'Use findings to adjust pricing and promotions.',
    ],
  },
  performance: {
    title: 'Performance',
    steps: [
      'Track completion and cancellation for Internal and External work.',
      'Watch repeat-client rates and peak hours by channel.',
      'Improve weak areas to win more clients.',
    ],
  },
  reports: {
    title: 'Reports',
    steps: [
      'Preview bookings, revenue, clients, or services by channel.',
      'Download Excel for a multi-sheet workbook (Summary, Channel, Services, Bookings).',
      'Share with your accountant — External stays outside marketplace payouts.',
    ],
  },
  insights: {
    title: 'Insights',
    steps: [
      'Actionable tips based on your recent activity.',
      'Act on suggestions to grow bookings.',
      'Check back as your data grows.',
    ],
  },
  notifications: {
    title: 'Notifications',
    steps: [
      'Booking, payment, and support alerts land here.',
      'Mark items read so you do not miss new requests.',
      'Tune email/SMS preferences under Preferences.',
    ],
  },
  messages: {
    title: 'Messages',
    steps: [
      'Chat with clients about upcoming events.',
      'Reply promptly to keep bookings on track.',
      'Open a booking for full event context when needed.',
    ],
  },
  preferences: {
    title: 'Preferences',
    steps: [
      'Choose which alerts you get by email or in-app.',
      'Keep booking and payment notices on so you never miss work.',
      'You can change these anytime.',
    ],
  },
  security: {
    title: 'Security',
    steps: [
      'Update your password regularly.',
      'Enable two-factor authentication when available.',
      'Sign out of devices you no longer use.',
    ],
  },
  settings: {
    title: 'Settings',
    steps: [
      'Manage account security and login options.',
      'For business details and logo, use Account instead.',
      'Prefer Preferences for notification channels.',
    ],
  },
  account: {
    title: 'What to update here',
    steps: [
      'Profile Information — your name, photo, email, and phone.',
      'Business Profile — business name, address, and logo for invoices & contracts.',
      'Operating location — so clients can find you nearby.',
      'Terms & Conditions — the terms clients agree to when booking.',
    ],
  },
  profile: {
    title: 'Profile preview',
    steps: [
      'This page is view-only — a preview of your details.',
      'To edit, open Account from the sidebar.',
      'Keep your logo and address current for invoices and contracts.',
    ],
  },
  branding: {
    title: 'Custom branding',
    steps: [
      'Set colors and imagery that match your brand (paid plan).',
      'Your business logo still comes from Account → Business.',
      'Preview how clients see your public presence.',
    ],
  },
  multiLocation: {
    title: 'Multi-location',
    steps: [
      'Add extra service areas beyond your primary location (paid plan).',
      'Keep each address accurate for local discovery.',
      'Primary location is also managed under Account.',
    ],
  },
  subscription: {
    title: 'Plans & pricing',
    steps: [
      'Compare Free vs paid features side by side.',
      'Upgrade to unlock branding, advanced analytics, and more.',
      'Manage renewal from Settings → Subscription after you upgrade.',
    ],
  },
  mySubscription: {
    title: 'Your subscription',
    steps: [
      'Review your current plan and live countdown to renew / expire.',
      'Toggle auto-renewal (synced with Stripe when billing is card-based).',
      'Renew, compare plans, or cancel from this page when needed.',
    ],
  },
  planFeatures: {
    title: 'Your plan features',
    steps: [
      'See what your current plan includes.',
      'Limits (services, images, etc.) are enforced automatically.',
      'Upgrade anytime from Plans & pricing.',
    ],
  },
  advancedBookings: {
    title: 'Advanced booking tools',
    steps: [
      'Extra booking workflows available on paid plans.',
      'Use alongside regular Bookings and Calendar.',
      'Keep statuses updated so clients stay informed.',
    ],
  },
  prioritySupport: {
    title: 'Priority support',
    steps: [
      'Open tickets for faster help on paid plans.',
      'Include screenshots and booking references when useful.',
      'Watch Notifications for replies.',
    ],
  },
  support: {
    title: 'Get help',
    steps: [
      'Browse common answers or contact support.',
      'Paid plans may include priority ticket support.',
      'Include clear steps to reproduce any issue.',
    ],
  },
  bookingHistory: {
    title: 'Booking history',
    steps: [
      'Past and completed bookings appear here.',
      'Open any row for full event and payment details.',
      'Use filters to find a specific client or date range.',
    ],
  },
  bookingRequests: {
    title: 'Booking requests',
    steps: [
      'New requests waiting for your response.',
      'Accept or decline promptly so clients can plan.',
      'Accepted jobs move into Bookings and Calendar.',
    ],
  },
  verification: {
    title: 'Vendor verification',
    steps: [
      'Submit business, contact, and tax details for your market.',
      'Upload clear identity and license documents.',
      'Once approved, full vendor tools unlock in the sidebar.',
    ],
  },
};
